import type { Campaign, Pitch, PitchStatus, Talent, User } from "../../shared/types";
import { isCampaignOwner } from "../campaignPermissions";

export type CampaignReviewContext = Pick<Campaign, "createdById" | "booker">;

export type CampaignPitchingState = Pick<Campaign, "pitchingOpen">;

const OPEN_PITCH_STATUSES: PitchStatus[] = ["pitched", "needs-info"];

/** Opportunities index: managers, sales, brand, and admin. Bookers do not browse. */
export function canBrowseOpportunities(user: User | undefined): boolean {
    return Boolean(
        user?.roles?.manager
        || user?.roles?.salesTeam
        || user?.roles?.brandTeam
        || user?.roles?.admin,
    );
}

export function isPitchReviewer(
    user: User | undefined,
    campaign: CampaignReviewContext,
): boolean {
    if (!user?.id) {
        return false;
    }
    if (user.roles?.admin || user.roles?.salesTeam || user.roles?.brandTeam) {
        return true;
    }
    if (campaign.booker?.uid === user.id) {
        return true;
    }
    return isCampaignOwner(user, campaign);
}

/** Pitcher, a manager on the creator, or Grail team. Opportunity creator is not an editor. */
export function isPitchEditor(
    user: User | undefined,
    pitch: Pick<Pitch, "managerId">,
    talent?: Pick<Talent, "managerIds">,
): boolean {
    if (!user?.id) {
        return false;
    }
    if (user.roles?.admin || user.roles?.salesTeam || user.roles?.brandTeam) {
        return true;
    }
    if (pitch.managerId === user.id) {
        return true;
    }
    return Boolean(talent?.managerIds?.includes(user.id));
}

/** Who may open Add Pitch and submit while pitching is open: managers plus Grail team. */
export function canPitchCreators(
    user: User | undefined,
    campaign: CampaignPitchingState,
): boolean {
    if (!campaign.pitchingOpen || !user?.roles) {
        return false;
    }
    return Boolean(
        user.roles.manager || user.roles.admin || user.roles.salesTeam || user.roles.brandTeam,
    );
}

export function canEditPitch(
    user: User | undefined,
    pitch: Pick<Pitch, "status" | "managerId">,
    talent?: Pick<Talent, "managerIds">,
): boolean {
    if (!pitch.status || !OPEN_PITCH_STATUSES.includes(pitch.status)) {
        return false;
    }
    return isPitchEditor(user, pitch, talent);
}

export function canWithdrawPitch(
    user: User | undefined,
    pitch: Pick<Pitch, "status" | "managerId">,
    talent?: Pick<Talent, "managerIds">,
): boolean {
    if (!pitch.status || !OPEN_PITCH_STATUSES.includes(pitch.status)) {
        return false;
    }
    return isPitchEditor(user, pitch, talent);
}

export function canResubmitPitch(
    user: User | undefined,
    pitch: Pick<Pitch, "status" | "managerId">,
    talent?: Pick<Talent, "managerIds">,
): boolean {
    if (pitch.status !== "withdrawn") {
        return false;
    }
    return isPitchEditor(user, pitch, talent);
}

/** Hide rejected and withdrawn campaign-tab rows unless the viewer is a reviewer, the pitcher, or on the creator. */
export function isHiddenCampaignPitch(
    user: User | undefined,
    campaign: CampaignReviewContext,
    pitch: Pick<Pitch, "status" | "managerId">,
    talent?: Pick<Talent, "managerIds">,
): boolean {
    if (pitch.status !== "rejected" && pitch.status !== "withdrawn") {
        return false;
    }
    return !isPitchReviewer(user, campaign) && !isPitchEditor(user, pitch, talent);
}

export type PitchAction = "approve" | "needs-info" | "edit" | "withdraw" | "reject" | "unreject" | "resubmit";

/** Row menu for one pitch. Reviewer actions and editor actions are a union. */
export function pitchActions(
    user: User | undefined,
    campaign: CampaignReviewContext,
    pitch: Pick<Pitch, "status" | "managerId" | "bookingId">,
    talent?: Pick<Talent, "managerIds">,
    talentAlreadyBooked = false,
): PitchAction[] {
    const actions: PitchAction[] = [];
    const reviewer = isPitchReviewer(user, campaign);
    const status = pitch.status;

    if (reviewer && status === "pitched" && !pitch.bookingId && !talentAlreadyBooked && talent) {
        actions.push("approve");
    }
    if (reviewer && status === "pitched") {
        actions.push("needs-info");
    }
    if (canEditPitch(user, pitch, talent)) {
        actions.push("edit");
    }
    if (canWithdrawPitch(user, pitch, talent)) {
        actions.push("withdraw");
    }
    if (reviewer && (status === "pitched" || status === "needs-info")) {
        actions.push("reject");
    }
    if (reviewer && status === "rejected") {
        actions.push("unreject");
    }
    if (canResubmitPitch(user, pitch, talent)) {
        actions.push("resubmit");
    }
    return actions;
}
